import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import DateRangePicker from '@wojtekmaj/react-daterange-picker/dist/DateRangePicker';
import { v1 as uuidv1 } from 'uuid';

import '../../Assets/Pages/campagneSdr.css';

import { fetchApi, withAccessToken } from '../../Network/NetworkService';
import toIsoDate from '../../Services/toIsoDate';
import { notif } from '../Ui/Notification';
import { endpoint, rqState } from '../../Constants';
import { Modal } from 'react-bootstrap';

// Per-recipient tokens a bulk campaign can substitute. Must stay in step with
// RECIPIENT_TOKENS in server/Controllers/Utils/WhatsappDrain.js — the server is
// the authority; these are only the chips offered to the operator.
const WA_TOKENS = [
    { token: '[prenom]', label: 'Prénom' },
    { token: '[nom]', label: 'Nom' }
];

// Campaign status, rendered. `deferred` is deliberately not styled as an error:
// hitting Meta's 24h allowance is the expected outcome of a large run, not a
// failure — the drain resumes on its own.
const waStatusLabel = (status) => ({
    pending: 'En attente',
    running: 'Envoi en cours',
    deferred: 'Différée — quota WhatsApp atteint',
    completed: 'Terminée',
    aborted: 'Interrompue'
}[status] || status);

// Meta's per-NUMBER quality rating. Coloured rather than printed plain because
// it is the one field that decides whether a campaign should be launched at
// all: RED is the last rung before Meta restricts the number, and it is driven
// by recipient Block/Report signals, not by delivery failures.
// Meta's own per-number send verdict, which is the authority on whether a
// number can send. LIMITED is deliberately amber rather than red: the number
// still sends, its messaging limit is just cut or at risk.
const waHealthLabel = (h) => ({
    AVAILABLE: 'possible',
    LIMITED: 'limité',
    BLOCKED: 'bloqué'
}[h] || h);

const waHealthColor = (h) => ({
    AVAILABLE: '#27ae60',
    LIMITED: '#f39c12',
    BLOCKED: '#c0392b'
}[h] || '#888');

const waQualityColor = (rating) => ({
    GREEN: '#27ae60',
    YELLOW: '#f39c12',
    RED: '#e74c3c'
}[rating] || '#7f8c8d');

const waStatusColor = (status) => ({
    pending: '#888',
    running: '#FAA265',
    deferred: '#d18b00',
    completed: '#27ae60',
    aborted: '#e74c3c'
}[status] || '#888');

/**
 * The delivery follow-up buckets. These PARTITION the campaign — every
 * recipient is in exactly one, and they sum to recipientCount — which is what
 * lets the operator reconcile a run against Meta's own dashboard and see where
 * a discrepancy comes from. Order is the funnel: best outcome first.
 *
 * `Ouvert` is a FLOOR, not a true open count. WhatsApp only reports a read
 * receipt when the recipient has them enabled in their privacy settings; with
 * them off the row stops at `delivered` no matter how many times it was read.
 * So the honest open rate is measured against `Reçu`, and is a lower bound —
 * the UI says so rather than leaving someone to infer it.
 */
const WA_DELIVERY_VIEWS = [
    { key: 'read', label: 'Ouvert', color: '#27ae60', hint: 'Lu sur le téléphone (minimum réel)' },
    { key: 'delivered', label: 'Reçu', color: '#2d8cf0', hint: 'Arrivé sur le téléphone, pas encore ouvert' },
    { key: 'awaiting', label: 'En attente retour', color: '#95a5a6', hint: 'Accepté par Meta, aucun retour encore — se résorbe seul' },
    { key: 'failed', label: 'Non distribué', color: '#e74c3c', hint: 'Meta a accepté puis refusé la distribution. Facturé.' },
    { key: 'queue', label: 'En file', color: '#FAA265', hint: 'Pas encore envoyé' },
    { key: 'skipped', label: 'Ignoré', color: '#b0b7bd', hint: 'Jamais tenté : sans consentement ou numéro inutilisable. Gratuit.' }
];

// Reads the bucket out of the two tallies the API returns. `counts` is what we
// did (send side), `delivery` is what happened to it (webhook side); they are
// deliberately not merged server-side, so the mapping lives here.
const waBucketCount = (progress, key) => {
    if (!progress) return 0;
    const c = progress.counts || {};
    const d = progress.delivery || {};
    if (key === 'queue') return (c.pending || 0) + (c.sending || 0);
    if (key === 'skipped') return c.skipped || 0;
    if (key === 'awaiting') return (d.pending || 0) + (d.accepted || 0) + (d.sent || 0);
    return d[key] || 0;
};

const typeStates = [
    { value: '', label: 'Tous' },
    { value: 'acne', label: 'acne' },
    { value: 'asthme', label: 'asthme' },
    { value: 'cardiologie', label: 'cardiologie' },
    { value: 'conjonctivite', label: 'conjonctivite' },
    { value: 'contraception', label: 'contraception' },
    { value: 'covid19', label: 'covid19' },
    { value: 'cystite', label: 'cystite' },
    { value: 'dentiste', label: 'dentiste' },
    { value: 'dermatologie', label: 'dermatologie' },
    { value: 'douleurs-de-dos', label: 'douleurs-de-dos' },
    { value: 'dysfonctionnement-erectile', label: 'dysfonctionnement-erectile' },
    { value: 'generaliste', label: 'generaliste' },
    { value: 'gynecologie', label: 'gynecologie' },
    { value: 'hemorroides', label: 'hemorroides' },
    { value: 'herpes', label: 'herpes' },
    { value: 'infection urinaire', label: 'infection urinaire' },
    { value: 'maladie-sexuelle', label: 'maladie-sexuelle' },
    { value: 'ophtalmologie', label: 'ophtalmologie' },
    { value: 'pediatrie', label: 'pediatrie' },
    { value: 'psychologie', label: 'psychologie' },
    { value: 'test-de-grossesse', label: 'test-de-grossesse' },
];

const userOpt = [
    { value: 'new', label: 'Nouveau' },
    { value: 'recurrent', label: 'Récurrent' },
];

const referralFilterOpt = [
    { value: 'all', label: 'Tous les utilisateurs' },
    { value: 'withCode', label: 'Avec code de parrainage' },
    { value: 'withoutCode', label: 'Sans code de parrainage' },
];

const defaultReferralSubject = 'Nous espérons que votre consultation sur Feeli vous a été utile';

const defaultReferralBody = '<p>Si quelqu\'un dans votre entourage rencontre un problème de santé similaire, <strong>votre recommandation peut aider un proche à consulter plus rapidement.</strong></p>';

const selectStyles = {
    control: (s) => ({
        ...s,
        borderRadius: 8,
        borderColor: '#ddd',
        minHeight: 42,
        boxShadow: 'none',
        '&:hover': { borderColor: '#F2718A' }
    }),
    option: (s, state) => ({
        ...s,
        color: '#333',
        backgroundColor: state.isSelected ? '#F2718A' : state.isFocused ? '#fff0f3' : '#fff',
    }),
};


const CampagneSdr = (props) => {
    const [option, setOption] = useState('');
    const [mailOption, setmailOption] = useState('');
    const [reqState, setReqState] = useState('waiting');
    const [reqType, setReqType] = useState('');
    const [dateRange, setDateRange] = useState([new Date('2022-01-01').toISOString(), new Date('2022-02-01').toISOString()])
    const [doctorList, setDoctorList] = useState([])
    const [userList, setUserList] = useState([]);

    const [doctorSelected, setDoctorSelected] = useState(null);
    const [relanceSpecialty, setRelanceSpecialty] = useState('');
    const [relanceRecurrence, setRelanceRecurrence] = useState('');
    const [userType, setUserType] = useState('new');
    const [value, setValue] = useState({
        email: '',
        phone: '',
        subj: '',
        msg: ''
    });
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [userLoading, setUserLoading] = useState(false);
    const [sendProgress, setSendProgress] = useState('0');

    // Referral state
    const [referralFilter, setReferralFilter] = useState('all');
    const [referralDateRange, setReferralDateRange] = useState(null);
    const [referralCount, setReferralCount] = useState(null);
    const [referralPreviewLoading, setReferralPreviewLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testSending, setTestSending] = useState(false);

    // Referral advanced filters
    const [refSpecialty, setRefSpecialty] = useState('');
    const [refDoctorId, setRefDoctorId] = useState('');
    const [refRecurrence, setRefRecurrence] = useState('');
    const [refFilterOptions, setRefFilterOptions] = useState({ specialties: [], doctors: [] });

    // WhatsApp
    // Which number this send goes out from. Quality rating and the 24h
    // messaging limit belong to a NUMBER, not to the account, so this is a real
    // choice rather than a setting — an operator picks the healthiest number
    // for the list they are about to send.
    const [waSenders, setWaSenders] = useState([]);
    const [waSender, setWaSender] = useState('');
    const [waStatus, setWaStatus] = useState(null);
    const [waStatusLoading, setWaStatusLoading] = useState(false);
    const [waTemplates, setWaTemplates] = useState([]);
    const [waTemplate, setWaTemplate] = useState('');
    const [waParams, setWaParams] = useState([]);
    // Values for a template's URL buttons, indexed by button. Separate from
    // waParams because Meta takes them in their own component — a template whose
    // button url carries {{n}} is rejected outright without one.
    const [waButtonParams, setWaButtonParams] = useState([]);
    const [waResult, setWaResult] = useState(null);
    // Bulk campaigns. Kept beside the single-send state rather than in their own
    // component because both halves share the template selection above them.
    const [waMode, setWaMode] = useState('single');
    const [waLabel, setWaLabel] = useState('');
    const [waLaunching, setWaLaunching] = useState(false);
    const [waCampaign, setWaCampaign] = useState(null);
    const [waCampaigns, setWaCampaigns] = useState([]);
    // Delivery follow-up. `waView` null keeps the recipient table closed —
    // opening it is an explicit click, because it lists patient names and
    // phone numbers and should not be on screen by default.
    const [waView, setWaView] = useState(null);
    const [waRecipients, setWaRecipients] = useState(null);
    const [waRecipientsLoading, setWaRecipientsLoading] = useState(false);
    // A target list handed over from "Suivi des relances" (the silent
    // recipients of one relance). While set, it replaces the period/doctor
    // cohort of the bulk tab. Only ids arrive: consent and STOP are still
    // enforced by enqueueCampaign.
    const [waPreset, setWaPreset] = useState(null);

    const selectedWaTemplate = waTemplates.find((t) => t.name === waTemplate) || null;

    const reset = () => {
        setValue({ email: '', phone: '', msg: '', subj: '' });
        setDoctorSelected([]);
        setmailOption('');
        setRelanceSpecialty('');
        setRelanceRecurrence('');
        setReferralCount(null);
        setReferralFilter('all');
        setReferralDateRange(null);
        setRefSpecialty('');
        setRefDoctorId('');
        setRefRecurrence('');
        setTestEmail('');
        setShowPreview(false);
        setPreviewHtml('');
        setWaTemplate('');
        setWaParams([]);
        setWaButtonParams([]);
        setWaResult(null);
        setWaMode('single');
        setWaLabel('');
        setWaCampaign(null);
        setWaPreset(null);
    }

    // Status and templates are both PER SENDER: the rating belongs to the
    // number, and the template library belongs to the WABA above it, which two
    // numbers need not share. Split out of loadWhatsapp so switching numbers
    // re-reads both instead of leaving the previous number's answers on screen.
    const loadWaSenderDetail = (key) => {
        setWaStatusLoading(true);
        const q = key ? `?sender=${encodeURIComponent(key)}` : '';

        fetchApi(`/campagne-sdr/whatsapp/status${q}`)
            .then((res) => {
                setWaStatus(res.status);
                setWaStatusLoading(false);
            })
            .catch(() => {
                setWaStatus({ configured: false, blockers: ['Statut indisponible'] });
                setWaStatusLoading(false);
            });

        // Cleared before the request, not after it: a template approved on the
        // old number may not exist on the new one, and leaving the old list
        // selectable lets an operator launch a run where every single send
        // fails with 132001.
        setWaTemplates([]);
        setWaTemplate('');
        setWaParams([]);
        setWaButtonParams([]);
        fetchApi(`/campagne-sdr/whatsapp/templates${q}`)
            .then((res) => setWaTemplates(res.templates || []))
            .catch(() => {
                notif.addNotification('Erreur', 'Erreur de récupération des templates', 'warning');
            });
    }

    // Loaded on demand rather than on mount: these calls hit the Meta Graph API,
    // and the other four tabs have no reason to pay that latency.
    const loadWhatsapp = () => {
        // health=0 — the dropdown only needs names, and one Graph round trip per
        // number to fill a list the operator has not opened yet is latency spent
        // on nothing. The chosen number's real health is read below.
        fetchApi('/campagne-sdr/whatsapp/senders?health=0')
            .then((res) => {
                const list = res.senders || [];
                setWaSenders(list);
                const preferred = (list.find((x) => x.isDefault) || list[0] || {}).key || '';
                setWaSender(preferred);
                loadWaSenderDetail(preferred);
            })
            .catch(() => {
                // A server predating this route, or one that failed to answer.
                // Fall back to the default sender rather than leaving the tab
                // unusable — every route treats an absent key as the default.
                setWaSenders([]);
                setWaSender('');
                loadWaSenderDetail('');
            });
    }

    const changeWaSender = (key) => {
        setWaSender(key);
        setWaResult(null);
        loadWaSenderDetail(key);
    }

    // --- Managing the numbers themselves -------------------------------------
    //
    // Numbers are operational state, not deployment config: a second number is
    // the response to a quality drop, which is exactly when waiting on an
    // environment update and the restart it triggers is worst. So they are
    // edited here.
    const [waManaging, setWaManaging] = useState(false);
    const [waManaged, setWaManaged] = useState([]);
    const [waEnvSenders, setWaEnvSenders] = useState([]);
    const [waSecretsAvailable, setWaSecretsAvailable] = useState(false);
    const [waSecretKeyEnv, setWaSecretKeyEnv] = useState('WHATSAPP_CONFIG_KEY');
    const [waEditing, setWaEditing] = useState(null);
    const [waSaving, setWaSaving] = useState(false);

    const emptyWaSender = {
        _id: null, key: '', label: '', phoneNumberId: '',
        wabaId: '', defaultLang: '', note: '', accessToken: '', appSecret: '',
        clearToken: false, clearAppSecret: false
    };

    const loadWaManaged = () => {
        fetchApi('/campagne-sdr/whatsapp/senders/manage')
            .then((res) => {
                setWaManaged(res.senders || []);
                setWaEnvSenders(res.envSenders || []);
                setWaSecretsAvailable(!!res.secretsAvailable);
                if (res.secretKeyEnv) setWaSecretKeyEnv(res.secretKeyEnv);
            })
            .catch(() => notif.addNotification('Erreur', 'Liste des numéros indisponible', 'warning'));
    }

    const openWaManager = () => {
        const next = !waManaging;
        setWaManaging(next);
        setWaEditing(null);
        if (next) loadWaManaged();
    }

    // Credentials are write-only: the server never sends them back, so the form
    // opens with those fields blank on an EDIT and an untouched blank means
    // "leave whatever is stored alone". Prefilling them with a placeholder
    // would be worse — it reads as "a value is here" and saving it would write
    // the placeholder as a token.
    const editWaSender = (row) => {
        setWaEditing(Object.assign({}, emptyWaSender, row, {
            accessToken: '', appSecret: '', clearToken: false, clearAppSecret: false
        }));
    }

    const saveWaSender = () => {
        const form = waEditing;
        if (!form) return;
        if (!form.label || !form.phoneNumberId) {
            notif.addNotification('Erreur', 'Libellé et Phone Number ID requis', 'warning');
            return;
        }

        setWaSaving(true);
        const isNew = !form._id;
        const body = {
            label: form.label,
            phoneNumberId: form.phoneNumberId,
            wabaId: form.wabaId || '',
            defaultLang: form.defaultLang || '',
            note: form.note || ''
        };
        if (isNew) body.key = form.key;
        // Only sent when the operator actually typed something, so an edit that
        // does not touch them cannot blank a stored credential by omission.
        // Three states, and the middle one needs its own control: an untouched
        // blank means "leave what is stored alone", so without an explicit
        // clear there is no way to undo "gave this number its own token" — the
        // stale sealed value would keep winning over a corrected environment
        // one forever. An empty string is what the route reads as "clear".
        if (form.clearToken) body.accessToken = '';
        else if (form.accessToken) body.accessToken = form.accessToken;
        if (form.clearAppSecret) body.appSecret = '';
        else if (form.appSecret) body.appSecret = form.appSecret;

        fetchApi(isNew ? '/campagne-sdr/whatsapp/senders' : `/campagne-sdr/whatsapp/senders/${form._id}`, {
            method: isNew ? 'POST' : 'PUT',
            body: JSON.stringify(body)
        })
            .then(() => {
                setWaSaving(false);
                setWaEditing(null);
                loadWaManaged();
                // The picker above reads a different list, so it has to be told.
                loadWhatsapp();
                notif.addNotification('Enregistré', isNew ? 'Numéro ajouté' : 'Numéro mis à jour', 'success');
            })
            .catch((err) => {
                setWaSaving(false);
                const message = (typeof err === 'string' ? err : err && err.message) || 'Échec de l\'enregistrement';
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    const waSenderAction = (row, action, successText) => {
        fetchApi(`/campagne-sdr/whatsapp/senders/${row._id}/${action}`, { method: 'POST' })
            .then(() => {
                loadWaManaged();
                loadWhatsapp();
                notif.addNotification('OK', successText, 'success');
            })
            .catch((err) => {
                const message = (typeof err === 'string' ? err : err && err.message) || 'Action impossible';
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    // Reactivating is an ordinary field update, unlike deactivating — which has
    // to refuse while runs are still in flight and therefore has its own route.
    const reactivateWaSender = (row) => {
        fetchApi(`/campagne-sdr/whatsapp/senders/${row._id}`, {
            method: 'PUT',
            body: JSON.stringify({ active: true })
        })
            .then(() => { loadWaManaged(); loadWhatsapp(); })
            .catch(() => notif.addNotification('Erreur', 'Réactivation impossible', 'warning'));
    }

    // --- Importing a number from Meta ---------------------------------------
    //
    // Typing a 16-digit phone number id by hand is both tedious and the easiest
    // thing in this form to get silently wrong: a wrong id is accepted by the
    // form, inherits a valid token, and only fails at send time. Reading the
    // list from Meta removes the transcription entirely.
    const [waDiscovering, setWaDiscovering] = useState(false);
    const [waCandidates, setWaCandidates] = useState(null);

    const discoverWaNumbers = () => {
        setWaDiscovering(true);
        fetchApi('/campagne-sdr/whatsapp/senders/discover')
            .then((res) => {
                setWaCandidates(res.numbers || []);
                setWaDiscovering(false);
            })
            .catch((err) => {
                setWaDiscovering(false);
                const message = (typeof err === 'string' ? err : err && err.message) || 'Lecture impossible';
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    // Pre-fills the form rather than saving directly. The key is permanent and
    // campaigns reference it forever, so it stays a deliberate choice — and the
    // suggestion below is only a starting point.
    const [waSyncing, setWaSyncing] = useState(false);
    const [waSyncResult, setWaSyncResult] = useState(null);

    const syncWaSenders = () => {
        setWaSyncing(true);
        setWaSyncResult(null);
        fetchApi('/campagne-sdr/whatsapp/senders/sync', { method: 'POST', body: JSON.stringify({}) })
            .then((res) => {
                setWaSyncing(false);
                setWaSyncResult(res);
                setWaCandidates(null);
                loadWaManaged();
                loadWhatsapp();
                notif.addNotification(
                    'Synchronisé',
                    res.created.length
                        ? `${res.created.length} numéro(s) ajouté(s)`
                        : 'Aucun nouveau numéro',
                    'success'
                );
            })
            .catch((err) => {
                setWaSyncing(false);
                const message = (typeof err === 'string' ? err : err && err.message) || 'Synchronisation impossible';
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    const useWaCandidate = (n) => {
        const suggestedKey = String(n.displayPhoneNumber || '')
            .replace(/\D/g, '')
            .slice(-4);
        setWaEditing(Object.assign({}, emptyWaSender, {
            key: 'num' + suggestedKey,
            label: (n.verifiedName ? n.verifiedName + ' ' : '') + (n.displayPhoneNumber || ''),
            phoneNumberId: n.phoneNumberId,
            // Left blank on purpose when it matches the WABA we already read
            // with: blank means inherit, and pinning the same value would only
            // create a second place to update if the WABA ever changes.
            wabaId: ''
        }));
        setWaCandidates(null);
    }

    // --- Registering a number on the Cloud API -------------------------------
    //
    // The middle of the three gates Meta reports, and the only one a server can
    // close. Verification needs a one-time code that arrives on the phone, so it
    // stays in WhatsApp Manager; connection is Meta's own consequence of
    // registering. Keyed off the structured fields rather than off the blocker
    // text, which is a French sentence written for a human to read.
    const [waRegister, setWaRegister] = useState(null);

    const needsWaRegistration = (n) =>
        n.codeVerificationStatus === 'VERIFIED' && n.platformType !== 'CLOUD_API';

    const submitWaRegister = () => {
        if (!waRegister || waRegister.busy) return;
        setWaRegister(Object.assign({}, waRegister, { busy: true }));
        fetchApi('/campagne-sdr/whatsapp/senders/register', {
            method: 'POST',
            body: JSON.stringify({ phoneNumberId: waRegister.phoneNumberId, pin: waRegister.pin })
        })
            .then((res) => {
                setWaRegister(null);
                notif.addNotification(
                    'Enregistré',
                    res.alreadyRegistered
                        ? 'Ce numéro était déjà enregistré sur la Cloud API'
                        : `Numéro enregistré — statut Meta : ${(res.number && res.number.status) || 'inconnu'}`,
                    'success'
                );
                // Re-read the WABA rather than patching the row in place: the
                // verdict shown has to be Meta's, and registering can move more
                // than the one field that was clicked.
                discoverWaNumbers();
            })
            .catch((err) => {
                setWaRegister(Object.assign({}, waRegister, { busy: false }));
                const message = (typeof err === 'string' ? err : err && err.message) || 'Enregistrement impossible';
                notif.addNotification('Erreur', message, 'warning');
            });
    }



    // Appends a token to a body-variable input. Appends rather than replaces so
    // "Bonjour [prenom]" style values stay possible, and only offered in bulk
    // mode — a single send goes to one known person, where a literal is clearer.
    const insertWaToken = (index, token) => {
        const next = waParams.slice();
        next[index] = (next[index] || '') + token;
        setWaParams(next);
    };

    const loadWaCampaigns = () => {
        fetchApi('/campagne-sdr/whatsapp/campaigns?limit=10')
            .then((res) => setWaCampaigns(res.campaigns || []))
            .catch(() => setWaCampaigns([]));
    }

    const refreshWaCampaign = (id) => {
        fetchApi(`/campagne-sdr/whatsapp/campaign/${id}`)
            .then((res) => setWaCampaign({ campaign: res.campaign, counts: res.counts, delivery: res.delivery, sender: res.sender }))
            .catch(() => {});
    }

    const loadWaRecipients = (id, view, page) => {
        setWaRecipientsLoading(true);
        fetchApi(`/campagne-sdr/whatsapp/campaign/${id}/recipients?view=${view}&page=${page || 1}&limit=50`)
            .then((res) => {
                setWaRecipients({ rows: res.rows || [], total: res.total, page: res.page, pages: res.pages });
                setWaRecipientsLoading(false);
            })
            .catch(() => {
                setWaRecipients({ rows: [], total: 0, page: 1, pages: 1 });
                setWaRecipientsLoading(false);
            });
    }

    // Switching campaigns must drop the open recipient list — leaving it would
    // show one campaign's recipients under another campaign's header.
    const openWaCampaign = (id) => {
        setWaView(null);
        setWaRecipients(null);
        refreshWaCampaign(id);
    }

    // Clicking a bucket opens it; clicking the open one closes it again.
    const toggleWaView = (id, view) => {
        if (waView === view) {
            setWaView(null);
            setWaRecipients(null);
            return;
        }
        setWaView(view);
        loadWaRecipients(id, view, 1);
    }

    // --- What will actually be sent -----------------------------------------
    //
    // Three recipient numbers exist and they are routinely different: the ids
    // selected, the ones that pass every gate, and the ones that fit under the
    // cap. The button used to show the first and the run sent the second, so a
    // cohort of 163 silent patients messaged 118 people and nothing said so
    // until the report. This asks the server what a launch WOULD do, and every
    // count below comes from that answer rather than from userList.length.
    const [waPreview, setWaPreview] = useState(null);
    const [waPreviewLoading, setWaPreviewLoading] = useState(false);
    // Distinguished from "not asked yet": on a failed preview the launch must be
    // blocked rather than falling back to the old behaviour of sending blind.
    const [waPreviewError, setWaPreviewError] = useState(null);
    // Blank means "use the server default". Kept as a string so the field can
    // be emptied without it collapsing to 0.
    const [waLimit, setWaLimit] = useState('');

    const waUserIdsKey = userList.map((u) => u._id).join(',');
    // Keyed on content, not array identity: these are rebuilt on every render,
    // so depending on the arrays themselves re-ran the whole cohort pipeline
    // continuously. Only whether a value holds a [prenom]/[nom] token can change
    // the plan, never the literal text, so a keystroke in a template parameter
    // must not re-query every user and re-parse every number.
    const waParamsKey = waParams.join('\u0000') + '|' + waButtonParams.join('\u0000');

    // Guards against out-of-order responses. The debounce timer is cleared on
    // re-run, but a request already past it still resolves, and a slow earlier
    // reply landing after a fast later one would leave the button showing a
    // count the launch will not honour — the exact preview/launch disagreement
    // this feature exists to prevent.
    const waPreviewSeq = useRef(0);

    useEffect(() => {
        if (option !== 'whatsapp' || waMode !== 'bulk' || !userList.length) {
            waPreviewSeq.current += 1;          // abandon anything in flight
            setWaPreview(null);
            setWaPreviewError(null);
            setWaPreviewLoading(false);
            return undefined;
        }
        // Dropped immediately, before the debounce: holding the previous
        // cohort's numbers on screen while they describe a new selection is
        // worse than showing nothing, because the button stays enabled on them.
        setWaPreview(null);
        setWaPreviewError(null);
        setWaPreviewLoading(true);

        const seq = waPreviewSeq.current + 1;
        waPreviewSeq.current = seq;

        const timer = setTimeout(() => {
            fetchApi('/campagne-sdr/whatsapp/campaign/preview', {
                method: 'POST',
                body: JSON.stringify({
                    sender: waSender || undefined,
                    userIds: userList.map((u) => u._id),
                    bodyParams: waParams,
                    buttonParams: waButtonParams
                    // No limit sent: it changes nothing the server has to
                    // compute, and willSend is derived below from the counts.
                })
            })
                .then((res) => {
                    if (waPreviewSeq.current !== seq) return;
                    setWaPreview(res);
                    setWaPreviewLoading(false);
                })
                .catch((err) => {
                    if (waPreviewSeq.current !== seq) return;
                    setWaPreview(null);
                    setWaPreviewError(
                        (typeof err === 'string' ? err : err && err.message) || 'Vérification impossible'
                    );
                    setWaPreviewLoading(false);
                });
        }, 400);

        return () => clearTimeout(timer);
    }, [waUserIdsKey, waSender, option, waMode, waParamsKey]);

    // The cap is applied client-side for display only; the server applies it
    // again in planCohort and remains the authority. Derived rather than
    // re-fetched because it needs no server work: the limit only feeds a
    // comparison against a count already in hand.
    const waEffectiveLimit = (() => {
        if (!waPreview) return null;
        if (waLimit === '') return waPreview.limitDefault;
        const typed = parseInt(waLimit, 10);
        if (isNaN(typed) || typed < 1) return waPreview.limitDefault;
        return Math.min(typed, waPreview.limitMax);
    })();

    const waWillSend = waPreview
        ? Math.min(waPreview.counts.sendable, waEffectiveLimit)
        : null;
    const waHeldBack = waPreview ? Math.max(waPreview.counts.sendable - waWillSend, 0) : 0;

    const launchWaCampaign = () => {
        setWaLaunching(true);
        setWaCampaign(null);
        fetchApi('/campagne-sdr/whatsapp/campaign', {
            method: 'POST',
            body: JSON.stringify({
                sender: waSender || undefined,
                template: waTemplate,
                label: waLabel || null,
                userIds: userList.map((u) => u._id),
                bodyParams: waParams,
                buttonParams: waButtonParams,
                limit: waEffectiveLimit === null ? undefined : waEffectiveLimit
            })
        })
            .then((res) => {
                setWaCampaign({ campaign: res.campaign, counts: res.counts, delivery: res.delivery, sender: res.sender });
                // An imported list is spent once launched; keeping it loaded
                // leaves the same patients one click from a second campaign.
                setWaPreset(null);
                setWaView(null);
                setWaRecipients(null);
                setWaPreview(null);
                setWaPreviewError(null);
                setWaLimit('');
                setWaLaunching(false);
                loadWaCampaigns();
                // Not "sent": the drain works the outbox afterwards, and a run
                // can be deferred for hours before it finishes.
                notif.addNotification('Campagne créée', `${res.counts.pending} destinataire(s) en file`, 'success');
            })
            .catch((err) => {
                const message = (typeof err === 'string' ? err : err && err.message) || 'Erreur de création';
                setWaLaunching(false);
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    const abortWaCampaign = (id) => {
        fetchApi(`/campagne-sdr/whatsapp/campaign/${id}/abort`, { method: 'POST' })
            .then(() => {
                refreshWaCampaign(id);
                loadWaCampaigns();
                notif.addNotification('Campagne arrêtée', 'Les envois restants ne partiront pas', 'success');
            })
            .catch((err) => {
                const message = (typeof err === 'string' ? err : err && err.message) || 'Erreur';
                notif.addNotification('Erreur', message, 'warning');
            });
    }

    // Poll while the run is still live. A deferred campaign waits on Meta's
    // rolling 24h allowance and can sit here for hours, which is exactly why
    // progress is polled rather than streamed over SSE like the email campaigns.
    //
    // Two speeds, because a campaign stops sending long before it stops
    // changing: the send side finishes in minutes, then delivery callbacks
    // trickle in from Meta for hours. Polling only while the run is live would
    // freeze the follow-up at whatever it happened to be when the last message
    // went out.
    //
    // The slow lane runs only while rows are still `awaiting` a first callback,
    // which is a state that provably resolves. delivered → read has no such
    // bound — someone can open a message next week — so that one is left to the
    // refresh button rather than polled forever.
    useEffect(() => {
        if (!waCampaign || !waCampaign.campaign) return undefined;
        const status = waCampaign.campaign.status;
        const live = status === 'pending' || status === 'running' || status === 'deferred';
        const settling = waBucketCount(waCampaign, 'awaiting') > 0;
        if (!live && !settling) return undefined;
        const id = setInterval(() => refreshWaCampaign(waCampaign.campaign._id), live ? 5000 : 30000);
        return () => clearInterval(id);
    }, [waCampaign]);

    const sendWhatsapp = () => {
        setLoading(true);
        setWaResult(null);
        fetchApi('/campagne-sdr/whatsapp/send', {
            method: 'POST',
            body: JSON.stringify({
                sender: waSender || undefined,
                phone: value.phone,
                template: waTemplate,
                bodyParams: waParams,
                buttonParams: waButtonParams
            })
        })
            .then((res) => {
                setWaResult(res);
                notif.addNotification('Success', 'Message WhatsApp envoyé !', 'success');
                setLoading(false);
            })
            .catch((err) => {
                // The server passes Meta's own error text through — it names the
                // real cause (template not approved, no opt-in, tier exceeded).
                // fetchApi rejects with res.message, a bare string, so unwrap both
                // that and a genuine network Error.
                const message = (typeof err === 'string' ? err : err && err.message) || "Erreur d'envoi !";
                setWaResult({ success: false, message });
                notif.addNotification('Erreur', message, 'warning');
                setLoading(false);
            });
    };

    const getDoctors = () => {
        fetchApi('/campagne-sdr/doctorList')
        .then((res) => setDoctorList(res.doctors))
        .catch(() => {
            notif.addNotification('Erreur', 'Erreur de récupération des docteurs', 'warning');
        });
    }

    const getUsers = () => {
        setUserLoading(true);
        fetchApi('/campagne-sdr/userList', {
            method: 'POST',
            body: JSON.stringify({ date: dateRange, doctorSelected, specialty: relanceSpecialty, recurrence: relanceRecurrence })
        })
        .then((res) => {
            setUserList(res.users);
            setUserLoading(false);
        })
        .catch(() => {
            notif.addNotification('Erreur', 'Erreur de récupération', 'warning');
            setUserLoading(false);
        });
    }

    useEffect(() => {
        getDoctors();
        fetchApi('/referral-analytics/filters')
            .then(function(res) {
                if (res && res.success) {
                    setRefFilterOptions({
                        specialties: res.specialties || [],
                        doctors: res.doctors || []
                    });
                }
            })
            .catch(function() {});
    }, []);

    // Arriving from "Suivi des relances" with a target list: open the WhatsApp
    // bulk tab on it directly (the cohort effect below turns it into userList).
    useEffect(() => {
        const target = props.location && props.location.state && props.location.state.waTarget;
        if (!target || !Array.isArray(target.userIds)) return;
        // Consume it: BrowserRouter keeps location.state in window.history, so a
        // reload or back/forward would otherwise restore a list that was already
        // launched or removed — one click from a second billed campaign.
        props.history.replace(props.location.pathname);
        setOption('whatsapp');
        setWaMode('bulk');
        setWaPreset(target);
        setWaLabel(target.label || '');
        loadWhatsapp();
        loadWaCampaigns();
    }, []);

    // The WhatsApp bulk tab targets the same cohort as Relance and reuses
    // /userList rather than growing a second definition of "who counts".
    // A preset from "Suivi des relances" wins over the period/doctor cohort.
    useEffect(() => {
        if (waPreset) {
            setUserList(waPreset.userIds.map((id) => ({ _id: id })));
            return;
        }
        const wantsCohort = option === 'relance' || (option === 'whatsapp' && waMode === 'bulk');
        if (wantsCohort && doctorSelected && doctorSelected.value && !!dateRange) {
            getUsers();
        } else {
            setUserList([]);
        }
    }, [doctorSelected, dateRange, option, relanceSpecialty, relanceRecurrence, waMode, waPreset]);

    const sendRelance = async () => {
        const tokenId = uuidv1();
        const sendEvent = new EventSource(withAccessToken(`${endpoint}/campagne-sdr/sendMailProgress?token=${tokenId}`));

        const resetSend = () => {
            setLoading(false);
            setSendProgress('0');
            sendEvent.close();
        };

        setLoading(true);

        sendEvent.onmessage = (ev) => {
            if (ev.data === 'start') {
                fetchApi('/campagne-sdr/sendMessage/relance', {
                    method: 'POST',
                    body: JSON.stringify({
                        doctorSelected,
                        dateRange,
                        id: tokenId,
                        ...value
                    })
                })
                .catch(() => {
                    notif.addNotification('Erreur', "Erreur d'envoi !", 'warning');
                    resetSend();
                });
            } else if (ev.data === '100') {
                notif.addNotification('Success', 'Message envoyé !', 'success');
                resetSend();
                fetchApi('/campagne-sdr/setHistory', {
                    method: 'POST',
                    body: JSON.stringify({ date: dateRange, doctorSelected })
                });
            } else if (ev.data.includes('error')) {
                notif.addNotification('Erreur', ev.data.split('&')[1], 'warning');
                resetSend();
            } else {
                setSendProgress(ev.data);
            }
        };

        sendEvent.onerror = () => {
            notif.addNotification('Erreur', "Erreur d'envoi !", 'warning');
            resetSend();
        };
    };

    const send = () => {
        setLoading(true);
        fetchApi(`/campagne-sdr/sendMessage/${option}${mailOption ? `/${mailOption}` : ''}`, {
            method: 'POST',
            body: JSON.stringify({
                ...value,
                ...(mailOption === 'perRequest' ? { state: reqState, date: toIsoDate(date, 0, 1), type: reqType } : {}),
                ...(mailOption === 'perUser' ? { userType, date: toIsoDate(date + '-01', 0, 1) } : {}),
            })
        })
        .then(() => {
            notif.addNotification('Success', 'Message envoyé !', 'success');
            setLoading(false);
        })
        .catch(() => {
            notif.addNotification('Erreur', "Erreur d'envoi !", 'warning');
            setLoading(false);
        });
    };

    const previewReferral = () => {
        setReferralPreviewLoading(true);
        fetchApi('/campagne-sdr/referral/preview', {
            method: 'POST',
            body: JSON.stringify({
                filter: referralFilter,
                dateRange: referralDateRange,
                specialty: refSpecialty,
                doctorId: refDoctorId,
                recurrence: refRecurrence
            })
        })
        .then((res) => {
            setReferralCount(res.count);
            setReferralPreviewLoading(false);
        })
        .catch(() => {
            notif.addNotification('Erreur', 'Erreur de récupération', 'warning');
            setReferralPreviewLoading(false);
        });
    };

    const previewEmail = () => {
        if (!value.subj || !value.msg) return;
        setPreviewLoading(true);
        fetchApi('/campagne-sdr/referral/preview-email', {
            method: 'POST',
            body: JSON.stringify({ subj: value.subj, msg: value.msg })
        })
        .then((res) => {
            setPreviewHtml(res.html);
            setShowPreview(true);
            setPreviewLoading(false);
        })
        .catch(() => {
            notif.addNotification('Erreur', 'Erreur de génération de l\'aperçu', 'warning');
            setPreviewLoading(false);
        });
    };

    const sendTestEmail = () => {
        if (!testEmail || !value.subj || !value.msg) return;
        setTestSending(true);
        fetchApi('/campagne-sdr/referral/send-test', {
            method: 'POST',
            body: JSON.stringify({ subj: value.subj, msg: value.msg, email: testEmail })
        })
        .then(() => {
            notif.addNotification('Success', 'Email de test envoyé à ' + testEmail, 'success');
            setTestSending(false);
        })
        .catch(() => {
            notif.addNotification('Erreur', "Erreur d'envoi du test", 'warning');
            setTestSending(false);
        });
    };

    const sendReferral = () => {
        const tokenId = uuidv1();
        const sendEvent = new EventSource(withAccessToken(`${endpoint}/campagne-sdr/sendMailProgress?token=${tokenId}`));

        const resetSend = () => {
            setLoading(false);
            setSendProgress('0');
            sendEvent.close();
        };

        setLoading(true);

        sendEvent.onmessage = (ev) => {
            if (ev.data === 'start') {
                fetchApi('/campagne-sdr/sendMessage/referral', {
                    method: 'POST',
                    body: JSON.stringify({
                        subj: value.subj,
                        msg: value.msg,
                        filter: referralFilter,
                        dateRange: referralDateRange,
                        specialty: refSpecialty,
                        doctorId: refDoctorId,
                        recurrence: refRecurrence,
                        id: tokenId
                    })
                })
                .catch(() => {
                    notif.addNotification('Erreur', "Erreur d'envoi !", 'warning');
                    resetSend();
                });
            } else if (ev.data === '100') {
                notif.addNotification('Success', 'Campagne de parrainage envoyée !', 'success');
                resetSend();
            } else if (ev.data.includes('error')) {
                notif.addNotification('Erreur', ev.data.split('&')[1], 'warning');
                resetSend();
            } else {
                setSendProgress(ev.data);
            }
        };

        sendEvent.onerror = () => {
            notif.addNotification('Erreur', "Erreur d'envoi !", 'warning');
            resetSend();
        };
    };

    const handleChange = (e) => {
        setValue({ ...value, [e.target.name]: e.target.value });
    };

    return (
        <div className="sdr-container">
            <h2 className="sdr-title">Campagne SDR</h2>

            <div className="sdr-tabs">
                <button className={'sdr-tab' + (option === 'email' ? ' active' : '')} onClick={() => { setOption('email'); reset(); }}>
                    <i className="zmdi zmdi-email" /> Email
                </button>
                <button className={'sdr-tab' + (option === 'sms' ? ' active' : '')} onClick={() => { setOption('sms'); reset(); }}>
                    <i className="zmdi zmdi-smartphone" /> SMS
                </button>
                <button className={'sdr-tab' + (option === 'whatsapp' ? ' active' : '')} onClick={() => { setOption('whatsapp'); reset(); loadWhatsapp(); }}>
                    <i className="zmdi zmdi-whatsapp" /> WhatsApp
                </button>
                <button className={'sdr-tab' + (option === 'relance' ? ' active' : '')} onClick={() => { setOption('relance'); reset(); }}>
                    <i className="zmdi zmdi-refresh-sync" /> Relance
                </button>
                <button className={'sdr-tab' + (option === 'referral' ? ' active' : '')} onClick={() => {
                    reset();
                    setOption('referral');
                    setValue({ email: '', phone: '', subj: defaultReferralSubject, msg: defaultReferralBody });
                }}>
                    <i className="zmdi zmdi-accounts" /> Parrainage
                </button>
            </div>

            {!option && (
                <div className="sdr-card">
                    <div className="sdr-empty">
                        <i className="zmdi zmdi-arrow-split" />
                        Sélectionnez un type de campagne ci-dessus
                    </div>
                </div>
            )}

            {/* ───── EMAIL TAB ───── */}
            {option === 'email' && (
                <div className="sdr-card">
                    <h3 className="sdr-card-title">Campagne Email</h3>
                    <p className="sdr-card-desc">Envoyez un email directement, par requête ou par type d'utilisateur.</p>

                    <div className="sdr-inline" style={{ marginBottom: 20 }}>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Mode d'envoi</label>
                            <select className="sdr-select" value={mailOption} onChange={(e) => setmailOption(e.target.value)}>
                                <option value="email">Par email</option>
                                <option value="perRequest">Par requête</option>
                                <option value="perUser">Par type d'utilisateur</option>
                            </select>
                        </div>

                        {mailOption === 'perRequest' && (
                            <>
                                <div className="sdr-form-group">
                                    <label className="sdr-label">État</label>
                                    <select className="sdr-select" onChange={(e) => setReqState(e.target.value)}>
                                        {rqState.map((v) => (
                                            <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Type</label>
                                    <select className="sdr-select" onChange={(e) => setReqType(e.target.value)}>
                                        {typeStates.map((v) => (
                                            <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Début</label>
                                    <input className="sdr-input" value={date} max={new Date().toISOString().split('T')[0]} onChange={(e) => setDate(e.target.value)} type="date" />
                                </div>
                            </>
                        )}

                        {mailOption === 'perUser' && (
                            <>
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Type d'utilisateur</label>
                                    <select className="sdr-select" onChange={(e) => setUserType(e.target.value)}>
                                        {userOpt.map((v) => (
                                            <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Mois</label>
                                    <input
                                        className="sdr-input"
                                        max={`${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}`}
                                        min="2021-01"
                                        onChange={(e) => setDate(e.target.value)}
                                        type="month"
                                        value={date}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {mailOption === 'email' && (
                        <div className="sdr-form-group">
                            <label className="sdr-label">Destinataire</label>
                            <input className="sdr-input" value={value.email} onChange={handleChange} name="email" type="text" placeholder="Email du destinataire" />
                        </div>
                    )}

                    <div className="sdr-form-group">
                        <label className="sdr-label">Sujet</label>
                        <input className="sdr-input" value={value.subj} onChange={handleChange} name="subj" type="text" placeholder="Sujet du mail" />
                    </div>

                    <div className="sdr-form-group">
                        <label className="sdr-label">Contenu</label>
                        <textarea className="sdr-textarea" value={value.msg} onChange={handleChange} name="msg" placeholder="Contenu du mail (HTML supporté)" />
                    </div>

                    <button
                        className="sdr-btn sdr-btn-primary"
                        onClick={send}
                        disabled={(mailOption === 'email' && !value.email) || !value.subj || !value.msg || loading}
                    >
                        {loading ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                </div>
            )}

            {/* ───── SMS TAB ───── */}
            {option === 'sms' && (
                <div className="sdr-card">
                    <h3 className="sdr-card-title">Campagne SMS</h3>
                    <p className="sdr-card-desc">Envoyez un SMS à un numéro de téléphone.</p>

                    <div className="sdr-form-group">
                        <label className="sdr-label">Téléphone</label>
                        <input className="sdr-input" value={value.phone} onChange={handleChange} name="phone" type="text" placeholder="Numéro de téléphone" />
                    </div>

                    <div className="sdr-form-group">
                        <label className="sdr-label">Message</label>
                        <textarea className="sdr-textarea" value={value.msg} onChange={handleChange} name="msg" placeholder="Contenu du SMS" />
                    </div>

                    <button
                        className="sdr-btn sdr-btn-primary"
                        onClick={send}
                        disabled={!value.phone || !value.msg || loading}
                    >
                        {loading ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                </div>
            )}

            {/* ───── WHATSAPP TAB ───── */}
            {option === 'whatsapp' && (
                <div className="sdr-card">
                    <h3 className="sdr-card-title">Campagne WhatsApp</h3>
                    <p className="sdr-card-desc">
                        Envoyez un template WhatsApp approuvé à un patient. Contrairement au SMS, le texte
                        libre est impossible : hors fenêtre de 24h ouverte par le patient, Meta n'autorise
                        que des templates pré-approuvés.
                    </p>

                    {/* The dropdown appears only once there is a choice to make;
                        the manage link is always there, because adding the SECOND
                        number is the thing this panel exists for. */}
                    {waSenders.length > 1 && (
                        <div className="sdr-form-group">
                            <label className="sdr-label">Numéro expéditeur</label>
                            <select
                                className="sdr-select"
                                value={waSender}
                                onChange={(e) => changeWaSender(e.target.value)}
                                disabled={waLaunching || loading}
                            >
                                {waSenders.map((s) => (
                                    <option key={s.key} value={s.key} disabled={!s.configured}>
                                        {s.label}
                                        {s.isDefault ? ' (défaut)' : ''}
                                        {s.configured ? '' : ' — non configuré'}
                                    </option>
                                ))}
                            </select>
                            <small className="sdr-hint">
                                La note de qualité et la limite d'envoi sur 24h appartiennent au numéro, pas au
                                compte : chaque numéro a les siennes. Changer de numéro ne répare pas une liste
                                ou un message qui fait bloquer — cela ne fait qu'y exposer un deuxième numéro.
                            </small>
                        </div>
                    )}

                    <div className="sdr-inline" style={{ marginBottom: 12 }}>
                        <button className="sdr-btn sdr-btn-secondary" onClick={openWaManager}>
                            {waManaging ? 'Fermer la gestion des numéros' : 'Gérer les numéros'}
                        </button>
                    </div>

                    {waManaging && (
                        <div className="sdr-stats-bar" style={{ display: 'block' }}>
                            <h4 style={{ margin: '0 0 12px' }}>Numéros expéditeurs</h4>

                            {!waSecretsAvailable && (
                                <p className="sdr-hint" style={{ color: '#c0392b' }}>
                                    <code>{waSecretKeyEnv}</code> n'est pas configuré : un numéro ne peut pas
                                    porter son propre token ici. Un numéro du même WABA hérite du token
                                    d'environnement et n'en a pas besoin.
                                </p>
                            )}

                            <table className="sdr-table">
                                <thead>
                                    <tr>
                                        <th>Clé</th><th>Libellé</th><th>Phone Number ID</th>
                                        <th>Identifiants</th><th>État</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {waManaged.map((row) => (
                                        <tr key={row._id} style={{ opacity: row.active ? 1 : 0.55 }}>
                                            <td><code>{row.key}</code>{row.isDefault ? <><br /><small>par défaut</small></> : null}</td>
                                            <td>{row.label}</td>
                                            <td><small>{row.phoneNumberId}</small></td>
                                            <td>
                                                <small>
                                                    {row.hasOwnToken ? 'token propre' : 'token hérité'}
                                                    {row.hasOwnAppSecret ? ' · secret propre' : ''}
                                                </small>
                                            </td>
                                            <td>{row.active ? 'Actif' : 'Désactivé'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button className="sdr-btn sdr-btn-secondary" onClick={() => editWaSender(row)}>
                                                    Modifier
                                                </button>
                                                {row.active && !row.isDefault && (
                                                    <button
                                                        className="sdr-btn sdr-btn-secondary"
                                                        onClick={() => waSenderAction(row, 'default', 'Expéditeur par défaut mis à jour')}
                                                    >
                                                        Par défaut
                                                    </button>
                                                )}
                                                {row.active && !row.isDefault && (
                                                    <button
                                                        className="sdr-btn sdr-btn-secondary"
                                                        onClick={() => waSenderAction(row, 'deactivate', 'Numéro désactivé')}
                                                    >
                                                        Désactiver
                                                    </button>
                                                )}
                                                {!row.active && (
                                                    <button className="sdr-btn sdr-btn-secondary" onClick={() => reactivateWaSender(row)}>
                                                        Réactiver
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Numbers that exist only in the environment. Listed so this
                                        table is the whole truth, and marked read-only so nobody
                                        edits a row here and wonders why nothing changed. */}
                                    {waEnvSenders.map((row) => (
                                        <tr key={'env-' + row.key} style={{ opacity: 0.7 }}>
                                            <td><code>{row.key}</code></td>
                                            <td>{row.label}</td>
                                            <td><small>{row.phoneNumberId}</small></td>
                                            <td><small>environnement</small></td>
                                            <td>Actif</td>
                                            <td><small style={{ color: '#888' }}>défini dans le .env</small></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {!waEditing && (
                                <div className="sdr-inline" style={{ marginTop: 12 }}>
                                    <button
                                        className="sdr-btn sdr-btn-primary"
                                        onClick={discoverWaNumbers}
                                        disabled={waDiscovering}
                                    >
                                        {waDiscovering ? 'Lecture du WABA...' : 'Importer depuis Meta'}
                                    </button>
                                    <button
                                        className="sdr-btn sdr-btn-secondary"
                                        onClick={syncWaSenders}
                                        disabled={waSyncing}
                                    >
                                        {waSyncing ? 'Synchronisation...' : 'Synchroniser'}
                                    </button>
                                    <button
                                        className="sdr-btn sdr-btn-secondary"
                                        onClick={() => setWaEditing(Object.assign({}, emptyWaSender))}
                                    >
                                        Saisir manuellement
                                    </button>
                                </div>
                            )}

                            {waSyncResult && (
                                <div className="sdr-stats-bar" style={{ display: 'block', marginTop: 12 }}>
                                    <strong>Synchronisation terminée</strong>
                                    {waSyncResult.created.length > 0 && (
                                        <ul style={{ margin: '8px 0 0 16px' }}>
                                            {waSyncResult.created.map((c) => (
                                                <li key={c.key}>
                                                    Ajouté : <strong>{c.label}</strong> (clé <code>{c.key}</code>)
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {/* Shown, not hidden: "nothing happened and here is
                                        why for each number" is the useful answer. A bare
                                        "0 added" leaves an operator wondering whether the
                                        button worked. */}
                                    {waSyncResult.skipped.length > 0 && (
                                        <ul style={{ margin: '8px 0 0 16px', color: '#666' }}>
                                            {waSyncResult.skipped.map((sk, i) => (
                                                <li key={i}><small>{sk.displayPhoneNumber} — {sk.reason}</small></li>
                                            ))}
                                        </ul>
                                    )}
                                    {waSyncResult.orphaned && waSyncResult.orphaned.length > 0 && (
                                        <p className="sdr-hint" style={{ color: '#f39c12', marginTop: 8 }}>
                                            Présents ici mais absents du WABA :{' '}
                                            {waSyncResult.orphaned.map((o) => o.label).join(', ')}. Rien n'a été
                                            modifié — désactivez-les manuellement si c'est voulu.
                                        </p>
                                    )}
                                    <button
                                        className="sdr-btn sdr-btn-secondary"
                                        style={{ marginTop: 8 }}
                                        onClick={() => setWaSyncResult(null)}
                                    >
                                        Fermer
                                    </button>
                                </div>
                            )}

                            {waCandidates && (
                                <div style={{ marginTop: 16 }}>
                                    <div className="sdr-divider" />
                                    <h4 style={{ margin: '0 0 4px' }}>Numéros trouvés sur le WABA</h4>
                                    <p className="sdr-hint" style={{ marginTop: 0 }}>
                                        Meta liste aussi les numéros qui ne peuvent pas encore envoyer. Ils sont
                                        affichés avec la raison plutôt que masqués. Un numéro vérifié mais pas
                                        encore enregistré sur la Cloud API peut être mis en service ici ; un numéro
                                        non vérifié demande le code reçu par SMS, dans WhatsApp Manager.
                                    </p>

                                    {!waCandidates.length && (
                                        <p className="sdr-hint">Aucun numéro renvoyé par Meta.</p>
                                    )}

                                    <table className="sdr-table">
                                        <thead>
                                            <tr>
                                                <th>Numéro</th><th>Qualité</th><th>État</th><th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {waCandidates.map((n) => (
                                                <tr key={n.phoneNumberId} style={{ opacity: n.usable ? 1 : 0.6 }}>
                                                    <td>
                                                        <strong>{n.displayPhoneNumber}</strong>
                                                        {n.verifiedName ? <><br /><small>{n.verifiedName}</small></> : null}
                                                        <br /><small style={{ color: '#888' }}>{n.phoneNumberId}</small>
                                                    </td>
                                                    <td>
                                                        <strong style={{ color: waQualityColor(n.qualityRating) }}>
                                                            {n.qualityRating || '—'}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        {n.blocker
                                                            ? <small style={{ color: '#c0392b' }}>{n.blocker}</small>
                                                            : <small style={{ color: '#27ae60' }}>Prêt à envoyer</small>}
                                                        {n.healthStatus && (
                                                            <><br /><small style={{ color: waHealthColor(n.healthStatus) }}>
                                                                Meta : envoi {waHealthLabel(n.healthStatus)}
                                                            </small></>
                                                        )}
                                                        {n.warning && (
                                                            <><br /><small style={{ color: '#f39c12' }}>{n.warning}</small></>
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {n.registered ? (
                                                            <small style={{ color: '#888' }}>
                                                                déjà ajouté
                                                                {n.registered.fromEnv ? ' (.env)' : ''}
                                                                <br />clé <code>{n.registered.key}</code>
                                                            </small>
                                                        ) : (
                                                            <button
                                                                className={'sdr-btn ' + (n.usable ? 'sdr-btn-primary' : 'sdr-btn-secondary')}
                                                                onClick={() => useWaCandidate(n)}
                                                            >
                                                                Utiliser
                                                            </button>
                                                        )}

                                                        {/* Offered independently of whether the number is already
                                                            a sender here: a row can perfectly well carry the right
                                                            phone number id and still be unregistered at Meta, which
                                                            is exactly the state that sends nothing while looking
                                                            fine in the admin. */}
                                                        {needsWaRegistration(n) && (
                                                            waRegister && waRegister.phoneNumberId === n.phoneNumberId ? (
                                                                <div style={{ marginTop: 8, whiteSpace: 'normal', maxWidth: 240 }}>
                                                                    <input
                                                                        className="sdr-input"
                                                                        type="password"
                                                                        inputMode="numeric"
                                                                        maxLength={6}
                                                                        autoComplete="off"
                                                                        placeholder="Code PIN (6 chiffres)"
                                                                        value={waRegister.pin}
                                                                        onChange={(e) => setWaRegister(Object.assign({}, waRegister, {
                                                                            pin: e.target.value.replace(/\D/g, '').slice(0, 6)
                                                                        }))}
                                                                    />
                                                                    <div style={{ marginTop: 6 }}>
                                                                        <button
                                                                            className="sdr-btn sdr-btn-primary"
                                                                            disabled={waRegister.busy || waRegister.pin.length !== 6}
                                                                            onClick={submitWaRegister}
                                                                        >
                                                                            {waRegister.busy ? 'Enregistrement...' : 'Valider'}
                                                                        </button>
                                                                        {' '}
                                                                        <button
                                                                            className="sdr-btn sdr-btn-secondary"
                                                                            disabled={waRegister.busy}
                                                                            onClick={() => setWaRegister(null)}
                                                                        >
                                                                            Annuler
                                                                        </button>
                                                                    </div>
                                                                    {/* Meta blocks PIN entry on a number after
                                                                        repeated failures, so a wrong guess costs
                                                                        more than a retry. */}
                                                                    <small className="sdr-hint" style={{ display: 'block', marginTop: 6 }}>
                                                                        Le PIN de validation en deux étapes du numéro.
                                                                        Ne le devinez pas : Meta bloque la saisie après
                                                                        plusieurs échecs.
                                                                    </small>
                                                                </div>
                                                            ) : (
                                                                <div style={{ marginTop: 8 }}>
                                                                    <button
                                                                        className="sdr-btn sdr-btn-secondary"
                                                                        onClick={() => setWaRegister({
                                                                            phoneNumberId: n.phoneNumberId,
                                                                            pin: '',
                                                                            busy: false
                                                                        })}
                                                                    >
                                                                        Enregistrer sur la Cloud API
                                                                    </button>
                                                                </div>
                                                            )
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <button
                                        className="sdr-btn sdr-btn-secondary"
                                        style={{ marginTop: 8 }}
                                        onClick={() => setWaCandidates(null)}
                                    >
                                        Fermer
                                    </button>
                                </div>
                            )}


                            {waEditing && (
                                <div style={{ marginTop: 16 }}>
                                    <div className="sdr-divider" />
                                    <h4 style={{ margin: '0 0 12px' }}>
                                        {waEditing._id ? `Modifier « ${waEditing.label} »` : 'Nouveau numéro'}
                                    </h4>

                                    {!waEditing._id && (
                                        <div className="sdr-form-group">
                                            <label className="sdr-label">Clé</label>
                                            <input
                                                className="sdr-input"
                                                value={waEditing.key}
                                                placeholder="es"
                                                onChange={(e) => setWaEditing(Object.assign({}, waEditing, { key: e.target.value }))}
                                            />
                                            <small className="sdr-hint">
                                                Minuscules, chiffres et _ uniquement. Définitive : les campagnes
                                                envoyées y font référence, donc elle ne pourra plus être modifiée.
                                            </small>
                                        </div>
                                    )}

                                    <div className="sdr-form-group">
                                        <label className="sdr-label">Libellé</label>
                                        <input
                                            className="sdr-input"
                                            value={waEditing.label}
                                            placeholder="Espagne"
                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, { label: e.target.value }))}
                                        />
                                    </div>

                                    <div className="sdr-form-group">
                                        <label className="sdr-label">Phone Number ID</label>
                                        <input
                                            className="sdr-input"
                                            value={waEditing.phoneNumberId}
                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, { phoneNumberId: e.target.value }))}
                                        />
                                        <small className="sdr-hint">
                                            Depuis WhatsApp Manager, pas le numéro affiché.
                                        </small>
                                    </div>

                                    <div className="sdr-form-group">
                                        <label className="sdr-label">WABA ID (optionnel)</label>
                                        <input
                                            className="sdr-input"
                                            value={waEditing.wabaId || ''}
                                            placeholder="hérite de l'environnement"
                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, { wabaId: e.target.value }))}
                                        />
                                        <small className="sdr-hint">
                                            À renseigner seulement si ce numéro est dans un autre WABA. Les
                                            templates appartiennent au WABA, pas au numéro.
                                        </small>
                                    </div>

                                    <div className="sdr-form-group">
                                        <label className="sdr-label">Langue par défaut (optionnel)</label>
                                        <input
                                            className="sdr-input"
                                            value={waEditing.defaultLang || ''}
                                            placeholder="fr"
                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, { defaultLang: e.target.value }))}
                                        />
                                    </div>

                                    {waSecretsAvailable && (
                                        <>
                                            <div className="sdr-form-group">
                                                <label className="sdr-label">Token (optionnel)</label>
                                                <input
                                                    className="sdr-input"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    value={waEditing.accessToken || ''}
                                                    placeholder={waEditing.hasOwnToken ? '•••••• (inchangé)' : "hérite de l'environnement"}
                                                    onChange={(e) => setWaEditing(Object.assign({}, waEditing, { accessToken: e.target.value }))}
                                                />
                                                <small className="sdr-hint">
                                                    Uniquement pour un numéro dans une autre app Meta. Chiffré avant
                                                    stockage et jamais réaffiché. Laisser vide conserve la valeur
                                                    actuelle.
                                                </small>
                                                {waEditing.hasOwnToken && (
                                                    <label className="sdr-hint" style={{ display: 'block', marginTop: 4 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!waEditing.clearToken}
                                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, {
                                                                clearToken: e.target.checked, accessToken: ''
                                                            }))}
                                                        />
                                                        {' '}Supprimer le token propre et hériter de l'environnement
                                                    </label>
                                                )}
                                            </div>

                                            <div className="sdr-form-group">
                                                <label className="sdr-label">App secret (optionnel)</label>
                                                <input
                                                    className="sdr-input"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    value={waEditing.appSecret || ''}
                                                    placeholder={waEditing.hasOwnAppSecret ? '•••••• (inchangé)' : "hérite de l'environnement"}
                                                    onChange={(e) => setWaEditing(Object.assign({}, waEditing, { appSecret: e.target.value }))}
                                                />
                                                <small className="sdr-hint">
                                                    Signe les webhooks de livraison. Propre à l'app Meta, pas au
                                                    numéro : deux numéros de la même app le partagent.
                                                </small>
                                                {waEditing.hasOwnAppSecret && (
                                                    <label className="sdr-hint" style={{ display: 'block', marginTop: 4 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!waEditing.clearAppSecret}
                                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, {
                                                                clearAppSecret: e.target.checked, appSecret: ''
                                                            }))}
                                                        />
                                                        {' '}Supprimer le secret propre et hériter de l'environnement
                                                    </label>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="sdr-form-group">
                                        <label className="sdr-label">Note (optionnel)</label>
                                        <input
                                            className="sdr-input"
                                            value={waEditing.note || ''}
                                            placeholder="Marché espagnol, chauffé depuis le 12/09"
                                            onChange={(e) => setWaEditing(Object.assign({}, waEditing, { note: e.target.value }))}
                                        />
                                    </div>

                                    <div className="sdr-inline">
                                        <button className="sdr-btn sdr-btn-primary" onClick={saveWaSender} disabled={waSaving}>
                                            {waSaving ? 'Enregistrement...' : 'Enregistrer'}
                                        </button>
                                        <button className="sdr-btn sdr-btn-secondary" onClick={() => setWaEditing(null)}>
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {waStatusLoading && <div className="sdr-stats-bar"><span>Vérification du compte WhatsApp...</span></div>}

                    {waStatus && !waStatus.configured && (
                        <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #e74c3c' }}>
                            <span>
                                <strong>WhatsApp non configuré.</strong> Renseignez <code>WHATSAPP_TOKEN</code> et{' '}
                                <code>WHATSAPP_PHONE_NUMBER_ID</code> dans le <code>.env</code> du serveur.
                            </span>
                        </div>
                    )}

                    {waStatus && waStatus.configured && (
                        <>
                            {waStatus.number && (
                                <div className="sdr-stats-bar">
                                    <span>
                                        Numéro : <strong>{waStatus.number.displayPhoneNumber}</strong>
                                        {waStatus.number.verifiedName ? ` (${waStatus.number.verifiedName})` : ''}
                                        {waStatus.number.qualityRating && (
                                            <>
                                                {' · qualité : '}
                                                <strong style={{ color: waQualityColor(waStatus.number.qualityRating) }}>
                                                    {waStatus.number.qualityRating}
                                                </strong>
                                            </>
                                        )}
                                        {waStatus.number.messagingLimitTier ? ` · palier : ${waStatus.number.messagingLimitTier}` : ''}
                                        {waStatus.number.healthStatus && (
                                            <>
                                                {' · envoi : '}
                                                <strong style={{ color: waHealthColor(waStatus.number.healthStatus) }}>
                                                    {waHealthLabel(waStatus.number.healthStatus)}
                                                </strong>
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* Non-blocking. Meta reports a cut or at-risk messaging
                                limit here; the number still sends, so this must not
                                look like, or gate the same thing as, a blocker. */}
                            {waStatus.warnings && waStatus.warnings.length > 0 && (
                                <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #f39c12' }}>
                                    <span>
                                        <strong>Avertissement Meta :</strong>
                                        <ul style={{ margin: '8px 0 0 16px' }}>
                                            {waStatus.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                        </ul>
                                    </span>
                                </div>
                            )}

                            {waStatus.blockers && waStatus.blockers.length > 0 && (
                                <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #e74c3c' }}>
                                    <span>
                                        <strong>Envoi impossible :</strong>
                                        <ul style={{ margin: '8px 0 0 16px' }}>
                                            {waStatus.blockers.map((b, i) => <li key={i}>{b}</li>)}
                                        </ul>
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    <div className="sdr-divider" />

                    <div className="sdr-form-group">
                        <label className="sdr-label">Type d'envoi</label>
                        <div className="sdr-inline">
                            <button
                                className={'sdr-btn ' + (waMode === 'single' ? 'sdr-btn-primary' : 'sdr-btn-secondary')}
                                onClick={() => { setWaMode('single'); setWaResult(null); }}
                            >
                                Envoi unitaire
                            </button>
                            <button
                                className={'sdr-btn ' + (waMode === 'bulk' ? 'sdr-btn-primary' : 'sdr-btn-secondary')}
                                onClick={() => { setWaMode('bulk'); setWaResult(null); loadWaCampaigns(); }}
                            >
                                Campagne en masse
                            </button>
                        </div>
                    </div>

                    <div className="sdr-form-group">
                        <label className="sdr-label">Template</label>
                        <select
                            className="sdr-select"
                            value={waTemplate}
                            onChange={(e) => {
                                setWaTemplate(e.target.value);
                                const tpl = waTemplates.find((t) => t.name === e.target.value);
                                setWaParams(tpl ? new Array(tpl.bodyVariables).fill('') : []);
                                setWaButtonParams(tpl && tpl.urlButtons ? tpl.urlButtons.map(() => '') : []);
                                setWaResult(null);
                            }}
                        >
                            <option value="">Sélectionner un template approuvé</option>
                            {waTemplates.map((t) => (
                                <option key={t.name} value={t.name}>
                                    {t.name} [{t.category}] — {t.approvedLanguages.join(', ')}
                                </option>
                            ))}
                        </select>
                        {waStatus && waStatus.configured && !waTemplates.length && (
                            <p className="sdr-hint">
                                Aucun template approuvé. Créez-en un dans WhatsApp Manager — le contenu SDR
                                relève de la catégorie MARKETING.
                            </p>
                        )}
                    </div>

                    {selectedWaTemplate && (
                        <>
                            {selectedWaTemplate.bodyText && (
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Aperçu du template</label>
                                    <div className="sdr-preview-info" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedWaTemplate.bodyText}
                                    </div>
                                </div>
                            )}

                            {selectedWaTemplate.urlButtons && selectedWaTemplate.urlButtons.some((b) => b.needsParam) && (
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Boutons</label>
                                    {selectedWaTemplate.urlButtons.map((b, i) => (
                                        b.needsParam ? (
                                            <div key={i} style={{ marginBottom: 8 }}>
                                                <input
                                                    className="sdr-input"
                                                    value={waButtonParams[i] || ''}
                                                    onChange={(e) => {
                                                        const next = waButtonParams.slice();
                                                        next[i] = e.target.value;
                                                        setWaButtonParams(next);
                                                    }}
                                                    type="text"
                                                    placeholder={`Valeur du lien « ${b.text} »`}
                                                />
                                                <p className="sdr-hint">{b.url}</p>
                                            </div>
                                        ) : null
                                    ))}
                                </div>
                            )}

                            {selectedWaTemplate.bodyVariables > 0 && (
                                <div className="sdr-form-group">
                                    <label className="sdr-label">Variables</label>
                                    {Array.from({ length: selectedWaTemplate.bodyVariables }).map((_, i) => (
                                        <div key={i} style={{ marginBottom: 12 }}>
                                            <input
                                                className="sdr-input"
                                                value={waParams[i] || ''}
                                                onChange={(e) => {
                                                    const next = waParams.slice();
                                                    next[i] = e.target.value;
                                                    setWaParams(next);
                                                }}
                                                type="text"
                                                placeholder={waMode === 'bulk'
                                                    ? `Valeur de {{${i + 1}}} — texte fixe ou [prenom]`
                                                    : `Valeur de {{${i + 1}}}`}
                                            />
                                            {waMode === 'bulk' && (
                                                <div className="sdr-inline" style={{ marginTop: 6, gap: 6 }}>
                                                    {WA_TOKENS.map((tk) => (
                                                        <button
                                                            key={tk.token}
                                                            type="button"
                                                            className="sdr-btn sdr-btn-secondary"
                                                            style={{ padding: '4px 10px', fontSize: 12 }}
                                                            onClick={() => insertWaToken(i, tk.token)}
                                                        >
                                                            + {tk.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {waMode === 'bulk' && (
                                        <p className="sdr-hint">
                                            Un texte fixe est envoyé tel quel à tout le monde. Utilisez
                                            [prenom] ou [nom] pour personnaliser chaque message ; les
                                            destinataires dont le champ est vide sont ignorés plutôt que
                                            de recevoir un message incomplet.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {waMode === 'single' && (
                    <div className="sdr-form-group">
                        <label className="sdr-label">Téléphone</label>
                        <input
                            className="sdr-input"
                            value={value.phone}
                            onChange={handleChange}
                            name="phone"
                            type="text"
                            placeholder="+33612345678"
                        />
                        <p className="sdr-hint">
                            La langue du template est choisie automatiquement selon la langue du patient
                            si son compte est retrouvé, sinon en français.
                        </p>
                    </div>
                    )}

                    {waMode === 'single' && waResult && waResult.success && (
                        <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #27ae60' }}>
                            <span>
                                Envoyé à <strong>{waResult.recipient || waResult.to}</strong> en{' '}
                                <strong>{waResult.language}</strong>
                                {waResult.wamid ? <> · <code>{waResult.wamid}</code></> : null}
                            </span>
                        </div>
                    )}

                    {waMode === 'single' && waResult && !waResult.success && (
                        <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #e74c3c' }}>
                            <span><strong>Échec :</strong> {waResult.message}</span>
                        </div>
                    )}

                    {waMode === 'single' && (
                    <button
                        className="sdr-btn sdr-btn-primary"
                        onClick={sendWhatsapp}
                        disabled={
                            !value.phone ||
                            !waTemplate ||
                            loading ||
                            !waStatus ||
                            !waStatus.configured ||
                            (waStatus.blockers && waStatus.blockers.length > 0) ||
                            (selectedWaTemplate && waParams.slice(0, selectedWaTemplate.bodyVariables).some((p) => !p)) ||
                            (selectedWaTemplate && (selectedWaTemplate.urlButtons || []).some((b, i) => b.needsParam && !waButtonParams[i]))
                        }
                    >
                        {loading ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                    )}

                    {waMode === 'bulk' && (
                        <>
                            <Modal show={userLoading} backdrop="static" keyboard={false} size="lg" className="modal-spinner">
                                <span className="loader" />
                                {userLoading && <p>Récupération des utilisateurs...</p>}
                            </Modal>

                            {waPreset && (
                                <div className="sdr-stats-bar">
                                    <span>
                                        Cible importée : sans réaction à la relance <strong>{waPreset.source}</strong>
                                        {typeof waPreset.reachable === 'number' && (
                                            <> ({waPreset.reachable} joignable(s) sur WhatsApp)</>
                                        )}
                                        {waPreset.approximate && (
                                            <><br />⚠️ Liste reconstituée (relance antérieure au suivi) : vérifiez-la avant d'envoyer.</>
                                        )}
                                        {/* Guarded, because the warning only means something above
                                            the safe batch size — showing it on a three-person list is
                                            warning fatigue on the one screen where warnings must be
                                            read. The real send count and the cap are stated by the
                                            preview bar below, from the server, so this does not
                                            repeat a number. */}
                                        {waPreset.reachable > 100 && (
                                            <><br />Audience froide : vérifiez la note de qualité du numéro avant d'augmenter la limite.</>
                                        )}
                                    </span>
                                    <button className="sdr-btn sdr-btn-secondary" onClick={() => { setWaPreset(null); setWaLabel(''); }}>
                                        Retirer
                                    </button>
                                </div>
                            )}

                            {!waPreset && (
                                <>
                                    <div className="sdr-inline" style={{ marginBottom: 12 }}>
                                        <div className="sdr-form-group">
                                            <label className="sdr-label">Période</label>
                                            <DateRangePicker locale="fr-FR" onChange={v => setDateRange(v)} value={dateRange} maxDate={new Date()} />
                                        </div>
                                        <div className="sdr-form-group" style={{ maxWidth: 400 }}>
                                            <label className="sdr-label">Médecin</label>
                                            <div className="sdr-select-wrap">
                                                <Select
                                                    value={doctorSelected}
                                                    isClearable
                                                    onChange={(v) => setDoctorSelected(v)}
                                                    options={doctorList.map((v) => ({ value: v._id, label: `${v.FName} ${v.LName} (${v.requestCount || 0})${v.activation ? '' : ' (désactivé)'}` }))}
                                                    isMulti={false}
                                                    styles={selectStyles}
                                                    placeholder="Sélectionner un médecin"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sdr-inline" style={{ marginBottom: 12 }}>
                                        <div className="sdr-form-group">
                                            <label className="sdr-label">Spécialité</label>
                                            <select className="sdr-select" value={relanceSpecialty} onChange={(e) => setRelanceSpecialty(e.target.value)}>
                                                <option value="">Toutes</option>
                                                {refFilterOptions.specialties.map((sp) => (
                                                    <option key={sp} value={sp}>{sp}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="sdr-form-group">
                                            <label className="sdr-label">Récurrence</label>
                                            <select className="sdr-select" value={relanceRecurrence} onChange={(e) => setRelanceRecurrence(e.target.value)}>
                                                <option value="">Non récurrents (par défaut)</option>
                                                <option value="recurring">Récurrents (2+ consultations)</option>
                                                <option value="all">Tous</option>
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="sdr-form-group">
                                <label className="sdr-label">Nom de la campagne (optionnel)</label>
                                <input
                                    className="sdr-input"
                                    value={waLabel}
                                    onChange={(e) => setWaLabel(e.target.value)}
                                    type="text"
                                    placeholder="Relance dermato septembre"
                                />
                            </div>

                            <div className="sdr-form-group" style={{ maxWidth: 280 }}>
                                <label className="sdr-label">
                                    Maximum de destinataires par campagne
                                </label>
                                <input
                                    className="sdr-input"
                                    type="number"
                                    min={1}
                                    max={waPreview ? waPreview.limitMax : undefined}
                                    value={waLimit}
                                    placeholder={waPreview ? String(waPreview.limitDefault) : '100'}
                                    onChange={(e) => setWaLimit(e.target.value.replace(/\D/g, ''))}
                                />
                                {/* The cap exists to protect the number's quality rating, so it
                                    is stated next to the rating it protects rather than as a
                                    bare number. A run of 100 from a GREEN number and from a RED
                                    one are not the same decision. */}
                                <p className="sdr-hint" style={{ marginTop: 4 }}>
                                    Vide = {waPreview ? waPreview.limitDefault : 100} par défaut.
                                    C'est la taille de reprise recommandée pendant qu'un numéro
                                    remonte en qualité.
                                    {waStatus && waStatus.number && waStatus.number.qualityRating && (
                                        <>
                                            {' Numéro actuel : '}
                                            <strong style={{ color: waQualityColor(waStatus.number.qualityRating) }}>
                                                {waStatus.number.qualityRating}
                                            </strong>
                                            {'.'}
                                        </>
                                    )}
                                </p>
                            </div>

                            {/* The honest count, from the server. Selected, sendable and
                                actually-sent are three different numbers and all three are
                                shown, because the gap between them is the thing an operator
                                otherwise discovers in the report afterwards. */}
                            <div className="sdr-stats-bar">
                                <span>
                                    Sélectionnés : <strong>{userList.length}</strong>
                                    {waPreviewLoading && <> · <em>calcul...</em></>}
                                    {waPreview && (
                                        <>
                                            {' · joignables : '}
                                            <strong>{waPreview.counts.sendable}</strong>
                                            {' · '}
                                            <strong style={{ color: waWillSend ? '#27ae60' : '#c0392b' }}>
                                                {waWillSend} message(s) envoyé(s)
                                            </strong>
                                            {waHeldBack > 0 && (
                                                <>
                                                    {' · '}
                                                    <strong style={{ color: '#f39c12' }}>
                                                        {waHeldBack} non retenu(s) (limite {waEffectiveLimit})
                                                    </strong>
                                                </>
                                            )}
                                        </>
                                    )}
                                </span>
                            </div>

                            {/* Without this the operator is back where they started: every
                                explanation below is gated on a successful preview, so a failed
                                one would leave the screen silent AND the button enabled. */}
                            {waPreviewError && (
                                <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #e74c3c' }}>
                                    <span>
                                        <strong>Vérification impossible :</strong> {waPreviewError}.
                                        Le nombre réel de destinataires est inconnu, l'envoi est donc bloqué.
                                        Réessayez ou rechargez la page.
                                    </span>
                                </div>
                            )}

                            {/* Unconditional. It was previously stated whatever the preview
                                said, and gating the only mention of the consent gate on a
                                successful round trip removed it exactly when it is needed. */}
                            <p className="sdr-hint">
                                Les destinataires sans consentement WhatsApp, sans numéro valide ou ayant
                                répondu STOP sont ignorés et comptés comme tels : aucun message ne leur est
                                envoyé. Le consentement est géré par feeli-server et n'est jamais écrit ici.
                            </p>

                            {waHeldBack > 0 && (
                                <p className="sdr-hint">
                                    Les {waHeldBack} personnes non retenues ne reçoivent aucune ligne dans cette
                                    campagne. Leurs identifiants sont enregistrés sur la campagne, donc elles
                                    restent identifiables pour un envoi suivant — mais relancer la même sélection
                                    réenverrait aux {waWillSend} premières, pas à elles. Vérifiez la note de
                                    qualité, puis augmentez la limite plutôt que de relancer à l'identique.
                                </p>
                            )}

                            {waPreview && (waPreview.counts.sendable < userList.length) && (
                                <p className="sdr-hint">
                                    Écartés avant envoi :
                                    {waPreview.counts.noConsent > 0 && <> {waPreview.counts.noConsent} sans consentement ·</>}
                                    {waPreview.counts.optedOut > 0 && <> {waPreview.counts.optedOut} ont répondu STOP ·</>}
                                    {waPreview.counts.invalidNumber > 0 && <> {waPreview.counts.invalidNumber} numéro invalide ·</>}
                                    {waPreview.counts.missingParams > 0 && <> {waPreview.counts.missingParams} personnalisation impossible ·</>}
                                    {waPreview.counts.missing > 0 && <> {waPreview.counts.missing} compte introuvable ·</>}
                                    {' '}comptés comme « ignorés » dans le rapport, aucun message ne leur est envoyé.
                                </p>
                            )}

                            <button
                                className="sdr-btn sdr-btn-primary"
                                onClick={launchWaCampaign}
                                disabled={
                                    !waTemplate ||
                                    !userList.length ||
                                    // Nothing would go out: every recipient is blocked. Letting
                                    // the click through creates a campaign of nothing but skipped
                                    // rows, which reads as a broken drain rather than as a cohort
                                    // with no consent.
                                    waWillSend === 0 ||
                                    // The real count is not known yet, or could not be fetched.
                                    // Launching here is the blind send this feature removes.
                                    waPreviewLoading ||
                                    !!waPreviewError ||
                                    !waPreview ||
                                    waLaunching ||
                                    !waStatus ||
                                    !waStatus.configured ||
                                    (waStatus.blockers && waStatus.blockers.length > 0) ||
                                    (selectedWaTemplate && waParams.slice(0, selectedWaTemplate.bodyVariables).some((prm) => !prm)) ||
                                    (selectedWaTemplate && (selectedWaTemplate.urlButtons || []).some((b, i) => b.needsParam && !waButtonParams[i]))
                                }
                            >
                                {waLaunching
                                    ? 'Création...'
                                    : waWillSend === null
                                        ? 'Lancer la campagne'
                                        : `Lancer la campagne (${waWillSend} message${waWillSend > 1 ? 's' : ''})`}
                            </button>

                            {waCampaign && waCampaign.campaign && (
                                <>
                                    <div className="sdr-divider" />
                                    <h3 className="sdr-card-title">
                                        {waCampaign.campaign.label || waCampaign.campaign.template}
                                    </h3>

                                    {/* Why the run is smaller than the selection. Without this
                                        the gap is only recoverable by arithmetic, and the fields
                                        recorded at enqueue to explain it would never be read.
                                        recipientCount counts the rows, skipped ones included;
                                        heldBack never had a row at all. */}
                                    {waCampaign.campaign.selectedCount > waCampaign.campaign.recipientCount && (
                                        <p className="sdr-hint">
                                            {waCampaign.campaign.selectedCount} sélectionné(s) ·{' '}
                                            {waCampaign.campaign.recipientCount} dans la campagne
                                            {waCampaign.campaign.heldBack > 0 && (
                                                <>
                                                    {' · '}
                                                    <strong style={{ color: '#f39c12' }}>
                                                        {waCampaign.campaign.heldBack} non retenu(s)
                                                    </strong>
                                                    {waCampaign.campaign.recipientLimit
                                                        ? ` (limite de ${waCampaign.campaign.recipientLimit})`
                                                        : ''}
                                                    , sans ligne dans cette campagne
                                                </>
                                            )}
                                        </p>
                                    )}

                                    {/* Which number paid for this run. Shown on the
                                        report, not only on the form: a quality drop is
                                        read here afterwards, and it belongs to one
                                        number rather than to the account. */}
                                    {waCampaign.sender && waSenders.length > 1 && (
                                        <p className="sdr-hint">
                                            Envoyée depuis : <strong>{waCampaign.sender.label}</strong>
                                            {waCampaign.sender.legacy ? ' (avant le multi-numéro)' : ''}
                                        </p>
                                    )}

                                    {/* A run bound to a retired number stalls rather than
                                        aborting — deactivating is reversible, aborting is
                                        not. Said out loud, or it reads as a drain that
                                        silently stopped working. */}
                                    {waCampaign.sender && waCampaign.sender.inactive && (
                                        <div className="sdr-stats-bar" style={{ borderLeft: '4px solid #f39c12' }}>
                                            <span>
                                                Le numéro <strong>{waCampaign.sender.label}</strong> est désactivé :
                                                cette campagne est en pause. Réactivez-le pour qu'elle reprenne.
                                            </span>
                                        </div>
                                    )}

                                    <div className="sdr-stats-bar" style={{ borderLeft: '4px solid ' + waStatusColor(waCampaign.campaign.status) }}>
                                        <span>
                                            <strong>{waStatusLabel(waCampaign.campaign.status)}</strong>
                                            {waCampaign.campaign.status === 'deferred' && waCampaign.campaign.deferredUntil ? (
                                                <> — reprise après {new Date(waCampaign.campaign.deferredUntil).toLocaleTimeString('fr-FR')}</>
                                            ) : null}
                                            {waCampaign.campaign.lastError ? <><br /><small>{waCampaign.campaign.lastError}</small></> : null}
                                        </span>
                                    </div>

                                    <div className="sdr-progress-wrap">
                                        <div className="sdr-progress">
                                            <div
                                                className="sdr-progress-bar"
                                                style={{ width: (waCampaign.campaign.recipientCount
                                                    ? Math.round(((waCampaign.counts.sent + waCampaign.counts.failed + waCampaign.counts.skipped) / waCampaign.campaign.recipientCount) * 100)
                                                    : 0) + '%' }}
                                            />
                                        </div>
                                        <p className="sdr-progress-text">
                                            {waCampaign.counts.sent} envoyé(s) · {waCampaign.counts.pending + waCampaign.counts.sending} en file ·{' '}
                                            {waCampaign.counts.failed} échec(s) · {waCampaign.counts.skipped} ignoré(s) ·{' '}
                                            {waCampaign.campaign.recipientCount} au total
                                        </p>
                                    </div>

                                    {/* Delivery follow-up. The bar above is what WE did; this is what
                                        happened to it. Both are shown because they routinely disagree —
                                        a run can be 100% "envoyé" and 0% received. */}
                                    <label className="sdr-label" style={{ marginTop: 18 }}>
                                        Suivi de distribution{' '}
                                        <button
                                            type="button"
                                            className="sdr-btn sdr-btn-secondary"
                                            style={{ marginLeft: 8, padding: '2px 10px', fontSize: 12 }}
                                            onClick={() => {
                                                refreshWaCampaign(waCampaign.campaign._id);
                                                if (waView) loadWaRecipients(waCampaign.campaign._id, waView, waRecipients ? waRecipients.page : 1);
                                            }}
                                        >
                                            Actualiser
                                        </button>
                                    </label>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {WA_DELIVERY_VIEWS.map((b) => {
                                            const n = waBucketCount(waCampaign, b.key);
                                            const active = waView === b.key;
                                            return (
                                                <button
                                                    key={b.key}
                                                    type="button"
                                                    title={b.hint + (n ? ' — cliquer pour voir la liste' : '')}
                                                    disabled={!n}
                                                    onClick={() => toggleWaView(waCampaign.campaign._id, b.key)}
                                                    style={{
                                                        flex: '1 1 130px',
                                                        textAlign: 'left',
                                                        padding: '10px 12px',
                                                        borderRadius: 8,
                                                        border: '1px solid ' + (active ? b.color : '#e4e6e8'),
                                                        borderLeft: '4px solid ' + b.color,
                                                        background: active ? '#fff8fa' : '#fff',
                                                        cursor: n ? 'pointer' : 'default',
                                                        opacity: n ? 1 : 0.5
                                                    }}
                                                >
                                                    <span style={{ display: 'block', fontSize: 20, fontWeight: 600, color: b.color }}>{n}</span>
                                                    <span style={{ display: 'block', fontSize: 12, color: '#666' }}>{b.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {waBucketCount(waCampaign, 'delivered') + waBucketCount(waCampaign, 'read') > 0 && (
                                        <p className="sdr-progress-text" style={{ marginTop: 10 }}>
                                            Taux d'ouverture ≥{' '}
                                            <strong>
                                                {Math.round((waBucketCount(waCampaign, 'read')
                                                    / (waBucketCount(waCampaign, 'read') + waBucketCount(waCampaign, 'delivered'))) * 100)}%
                                            </strong>{' '}
                                            des messages reçus.{' '}
                                            <small style={{ color: '#888' }}>
                                                Minimum : WhatsApp ne signale « lu » que si le destinataire a laissé
                                                les confirmations de lecture activées.
                                            </small>
                                        </p>
                                    )}

                                    {waView && (
                                        <div className="sdr-table-wrap" style={{ marginTop: 12 }}>
                                            {waRecipientsLoading && <p className="sdr-progress-text">Chargement...</p>}
                                            {!waRecipientsLoading && waRecipients && (
                                                <>
                                                    <table className="sdr-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Destinataire</th>
                                                                <th>Téléphone</th>
                                                                <th>Envoyé</th>
                                                                <th>Reçu</th>
                                                                <th>Ouvert</th>
                                                                <th>Détail</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {waRecipients.rows.map((r) => (
                                                                <tr key={r._id}>
                                                                    <td>
                                                                        {(r.FName || r.LName)
                                                                            ? `${r.FName || ''} ${r.LName || ''}`.trim()
                                                                            : <span style={{ color: '#999' }}>—</span>}
                                                                        {r.email ? <><br /><small style={{ color: '#888' }}>{r.email}</small></> : null}
                                                                    </td>
                                                                    <td>+{r.to}</td>
                                                                    <td>{r.sentAt ? new Date(r.sentAt).toLocaleString('fr-FR') : '—'}</td>
                                                                    <td>{r.deliveredAt ? new Date(r.deliveredAt).toLocaleString('fr-FR') : '—'}</td>
                                                                    <td>{r.readAt ? new Date(r.readAt).toLocaleString('fr-FR') : '—'}</td>
                                                                    <td>
                                                                        {r.metaCode ? <small>code {r.metaCode}{r.unmapped ? ' (inconnu)' : ''}</small> : null}
                                                                        {r.lastError ? <><br /><small style={{ color: '#888' }}>{r.lastError}</small></> : null}
                                                                        {!r.metaCode && !r.lastError ? '—' : null}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>

                                                    {waRecipients.pages > 1 && (
                                                        <p className="sdr-progress-text">
                                                            <button
                                                                className="sdr-btn sdr-btn-secondary"
                                                                disabled={waRecipients.page <= 1}
                                                                onClick={() => loadWaRecipients(waCampaign.campaign._id, waView, waRecipients.page - 1)}
                                                            >
                                                                Précédent
                                                            </button>
                                                            {' '}Page {waRecipients.page} / {waRecipients.pages} ({waRecipients.total}){' '}
                                                            <button
                                                                className="sdr-btn sdr-btn-secondary"
                                                                disabled={waRecipients.page >= waRecipients.pages}
                                                                onClick={() => loadWaRecipients(waCampaign.campaign._id, waView, waRecipients.page + 1)}
                                                            >
                                                                Suivant
                                                            </button>
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {['pending', 'running', 'deferred'].indexOf(waCampaign.campaign.status) !== -1 && (
                                        <button className="sdr-btn sdr-btn-secondary" onClick={() => abortWaCampaign(waCampaign.campaign._id)}>
                                            Arrêter la campagne
                                        </button>
                                    )}
                                </>
                            )}

                            {waCampaigns.length > 0 && (
                                <>
                                    <div className="sdr-divider" />
                                    <label className="sdr-label">Campagnes récentes</label>
                                    <div className="sdr-table-wrap">
                                        <table className="sdr-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Campagne</th>
                                                    <th>Destinataires</th>
                                                    <th>Statut</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {waCampaigns.map((c) => (
                                                    <tr key={c._id}>
                                                        <td>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</td>
                                                        <td>{c.label || c.template}</td>
                                                        <td>{c.recipientCount}</td>
                                                        <td style={{ color: waStatusColor(c.status) }}>{waStatusLabel(c.status)}</td>
                                                        <td>
                                                            <button className="sdr-btn sdr-btn-secondary" onClick={() => openWaCampaign(c._id)}>
                                                                Voir
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ───── RELANCE TAB ───── */}
            {option === 'relance' && (
                <div className="sdr-card">
                    <Modal
                        show={userLoading}
                        backdrop="static"
                        keyboard={false}
                        size="lg"
                        className="modal-spinner"
                    >
                        <span className="loader" />
                        {userLoading && <p>Récupération des utilisateurs...</p>}
                    </Modal>

                    <h3 className="sdr-card-title">
                        {relanceRecurrence === 'recurring' ? 'Relance des récurrents' : relanceRecurrence === 'all' ? 'Relance de tous les utilisateurs' : 'Relance des non récurrents'}
                    </h3>
                    <p className="sdr-card-desc">
                        {relanceRecurrence === 'recurring'
                            ? 'Relancez les utilisateurs ayant effectué plusieurs consultations.'
                            : relanceRecurrence === 'all'
                                ? 'Relancez tous les utilisateurs ayant consulté.'
                                : 'Relancez les utilisateurs qui n\'ont pas fait de seconde consultation.'}
                    </p>

                    <div className="sdr-inline" style={{ marginBottom: 12 }}>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Période</label>
                            <DateRangePicker locale="fr-FR" onChange={v => setDateRange(v)} value={dateRange} maxDate={new Date()} />
                        </div>
                        <div className="sdr-form-group" style={{ maxWidth: 400 }}>
                            <label className="sdr-label">Médecin</label>
                            <div className="sdr-select-wrap">
                                <Select
                                    value={doctorSelected}
                                    isClearable
                                    onChange={(v) => setDoctorSelected(v)}
                                    options={doctorList.map((v) => ({ value: v._id, label: `${v.FName} ${v.LName} (${v.requestCount || 0})${v.activation ? '' : ' (désactivé)'}` }))}
                                    isMulti={false}
                                    styles={selectStyles}
                                    placeholder="Sélectionner un médecin"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="sdr-inline" style={{ marginBottom: 20 }}>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Spécialité</label>
                            <select className="sdr-select" value={relanceSpecialty} onChange={(e) => setRelanceSpecialty(e.target.value)}>
                                <option value="">Toutes</option>
                                {refFilterOptions.specialties.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Récurrence</label>
                            <select className="sdr-select" value={relanceRecurrence} onChange={(e) => setRelanceRecurrence(e.target.value)}>
                                <option value="">Non récurrents (par défaut)</option>
                                <option value="recurring">Récurrents (2+ consultations)</option>
                                <option value="all">Tous</option>
                            </select>
                        </div>
                    </div>

                    {doctorSelected && doctorSelected.value && (
                        <div className="sdr-stats-bar">
                            <span>Utilisateurs ciblés : <strong>{userList.length}</strong></span>
                        </div>
                    )}

                    <div className="sdr-form-group">
                        <label className="sdr-label">Contenu du mail</label>
                        <textarea className="sdr-textarea" value={value.subj} onChange={handleChange} name="subj" placeholder="Contenu du mail de relance" />
                    </div>

                    {loading ? (
                        <div className="sdr-progress-wrap">
                            <div className="sdr-progress">
                                <div className="sdr-progress-bar" style={{ width: sendProgress + '%' }} />
                            </div>
                            <p className="sdr-progress-text">Envoi en cours : {sendProgress}%</p>
                        </div>
                    ) : (
                        <button
                            className="sdr-btn sdr-btn-primary"
                            onClick={sendRelance}
                            disabled={!doctorSelected || !userList.length || !dateRange || !value.subj || loading}
                        >
                            Envoyer la relance
                        </button>
                    )}
                </div>
            )}

            {/* ───── REFERRAL / PARRAINAGE TAB ───── */}
            {option === 'referral' && (
                <div className="sdr-card">
                    <h3 className="sdr-card-title">Campagne de parrainage</h3>
                    <p className="sdr-card-desc">
                        Envoyez un email aux utilisateurs pour les encourager à partager leur lien de parrainage.
                        Un code de parrainage sera automatiquement généré pour ceux qui n'en ont pas encore.
                    </p>

                    <div className="sdr-inline" style={{ marginBottom: 12 }}>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Filtrer les utilisateurs</label>
                            <select className="sdr-select" value={referralFilter} onChange={(e) => { setReferralFilter(e.target.value); setReferralCount(null); }}>
                                {referralFilterOpt.map((v) => (
                                    <option key={v.value} value={v.value}>{v.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Période d'inscription</label>
                            <DateRangePicker
                                locale="fr-FR"
                                onChange={v => { setReferralDateRange(v); setReferralCount(null); }}
                                value={referralDateRange}
                                maxDate={new Date()}
                            />
                        </div>
                    </div>
                    <div className="sdr-inline" style={{ marginBottom: 20 }}>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Spécialité</label>
                            <select className="sdr-select" value={refSpecialty} onChange={(e) => { setRefSpecialty(e.target.value); setReferralCount(null); }}>
                                <option value="">Toutes</option>
                                {refFilterOptions.specialties.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Médecin</label>
                            <select className="sdr-select" value={refDoctorId} onChange={(e) => { setRefDoctorId(e.target.value); setReferralCount(null); }}>
                                <option value="">Tous</option>
                                {refFilterOptions.doctors.map((d) => (
                                    <option key={d._id} value={d._id}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sdr-form-group">
                            <label className="sdr-label">Récurrence</label>
                            <select className="sdr-select" value={refRecurrence} onChange={(e) => { setRefRecurrence(e.target.value); setReferralCount(null); }}>
                                <option value="">Tous</option>
                                <option value="recurring">Récurrents (2+ consultations)</option>
                                <option value="single">Non récurrents (1 consultation)</option>
                            </select>
                        </div>
                        <div className="sdr-form-group" style={{ flex: '0 0 auto' }}>
                            <label className="sdr-label">&nbsp;</label>
                            <button className="sdr-btn sdr-btn-secondary" onClick={previewReferral} disabled={referralPreviewLoading}>
                                {referralPreviewLoading ? 'Chargement...' : 'Voir le nombre'}
                            </button>
                        </div>
                    </div>

                    {referralCount !== null && (
                        <div className="sdr-stats-bar">
                            <span>Utilisateurs ciblés : <strong>{referralCount}</strong></span>
                        </div>
                    )}

                    <div className="sdr-divider" />

                    <div className="sdr-form-group">
                        <label className="sdr-label">Sujet</label>
                        <input className="sdr-input" value={value.subj} onChange={handleChange} name="subj" type="text" placeholder="Ex: Parrainez vos proches et gagnez des avantages !" />
                    </div>

                    <div className="sdr-form-group">
                        <label className="sdr-label">Contenu du mail</label>
                        <textarea
                            className="sdr-textarea"
                            value={value.msg}
                            onChange={handleChange}
                            name="msg"
                            placeholder="Texte principal de l'email (HTML supporté). Le design, le bouton et le lien de parrainage sont automatiquement inclus dans le template."
                        />
                        <p className="sdr-hint">
                            Ce texte s'affiche entre le titre et l'image du docteur. Le bouton "Recommander à un proche" avec le lien personnalisé est ajouté automatiquement.
                        </p>
                    </div>

                    <div className="sdr-divider" />

                    <div className="sdr-inline" style={{ marginBottom: 20 }}>
                        <button
                            className="sdr-btn sdr-btn-secondary"
                            onClick={previewEmail}
                            disabled={!value.subj || !value.msg || previewLoading}
                        >
                            {previewLoading ? 'Chargement...' : 'Aperçu email'}
                        </button>

                        <div className="sdr-form-group" style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    className="sdr-input"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    type="email"
                                    placeholder="Email pour test"
                                    style={{ flex: 1 }}
                                />
                                <button
                                    className="sdr-btn sdr-btn-secondary"
                                    onClick={sendTestEmail}
                                    disabled={!testEmail || !value.subj || !value.msg || testSending}
                                >
                                    {testSending ? 'Envoi...' : 'Envoyer un test'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <Modal show={showPreview} onHide={() => { setShowPreview(false); setPreviewHtml(''); }} bsSize="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Aperçu de l'email</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="sdr-preview-info">
                                Ceci est un aperçu avec des données fictives (Jean Dupont, code A1B2C3D4).
                            </div>
                            <div className="sdr-preview-frame">
                                <iframe
                                    title="Email preview"
                                    srcDoc={previewHtml}
                                    sandbox="allow-same-origin"
                                    style={{ width: '100%', minHeight: 600, border: 'none' }}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button className="sdr-btn sdr-btn-secondary" onClick={() => setShowPreview(false)}>Fermer</button>
                        </Modal.Footer>
                    </Modal>

                    <div className="sdr-divider" />

                    {loading ? (
                        <div className="sdr-progress-wrap">
                            <div className="sdr-progress">
                                <div className="sdr-progress-bar" style={{ width: sendProgress + '%' }} />
                            </div>
                            <p className="sdr-progress-text">Envoi en cours : {sendProgress}%</p>
                        </div>
                    ) : (
                        <button
                            className="sdr-btn sdr-btn-primary"
                            onClick={sendReferral}
                            disabled={!value.subj || !value.msg || loading || referralCount === null || referralCount === 0}
                        >
                            Envoyer la campagne
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CampagneSdr;
